import { action, makeAutoObservable } from 'mobx';
import { logger } from '@cutwise/utils';

import { pureStructureFrom } from '@/common/helpers/pureStructureFrom';
import useStore from '@/common/hooks/useStore';
import ConfirmationFactory from '@/common/services/ConfirmationFactory';
import Notifier from '@/common/services/Notifier';
import ContactsAPI from '@/contacts/api/ContactsAPI';
import { ContactData, ContactDataModel } from '@/contacts/entity/ContactData';

class ContactsStore {
  contacts: ContactData[] = [];

  private readonly contactsAPI = new ContactsAPI();

  initContactsStore = () => {
    this.getUserContacts().catch((e) => logger.error(e));
  };

  getUserContacts = () => this.contactsAPI.getUserContacts().then(
    action((response) => {
      if (!response.content) {
        this.contacts = [];

        return;
      }

      this.contacts = response.content.map((contact: ContactDataModel) => new ContactData(contact));
    }),
  );

  createContact = (data: ContactDataModel) => this.contactsAPI.create(data).then(
    action((response) => {
      const newContact = new ContactData(response.content);

      this.contacts = [...this.contacts, newContact];

      return newContact;
    }),
  );

  editContact = (data: ContactDataModel) => this.contactsAPI.edit(data).then(
    action((response) => {
      const newContact = new ContactData(response.content);
      const nextContacts = pureStructureFrom(this.contacts);

      const targetIndex = nextContacts.findIndex(({ id }: ContactData) => id === data.id);

      if (targetIndex !== -1) {
        nextContacts[targetIndex] = newContact;
      }

      this.contacts = nextContacts;

      return newContact;
    }),
  );

  deleteContact = (id: string) => {
    ConfirmationFactory.makeDeleteConfirmation('contact')
      .then(
        action(() => {
          this.contactsAPI.remove(id).then(() => {
            this.getUserContacts().then(() => {
              Notifier.success('Contact was deleted.');
            });
          });
        }),
      )
      .catch(() => {
        /*
         * TODO
         * when close confirm model, Promise throw an error, probably we need to change this behavior
         * for now just do not do anything to avoid error logs in console
         * */
      });
  };

  constructor() {
    makeAutoObservable<ContactsStore, 'contactsAPI'>(this, {
      contactsAPI: false,
      initContactsStore: false,
      getUserContacts: false,
      createContact: false,
      editContact: false,
      deleteContact: false,
    });
  }
}

export const useContactsStore = () => useStore(ContactsStore);

export const contactsStore = new ContactsStore();
export default ContactsStore;
